import React, { createContext, useState, useContext, ReactNode, useEffect, useRef, useCallback } from 'react';
import { auth, db, messaging } from '../firebase/client';
import { getToken, onMessage } from 'firebase/messaging';

export type CustomNotificationPermission = 'granted' | 'denied' | 'default';
export type Role = 'client' | 'restaurateur' | 'livreur' | 'super_admin' | 'admin';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    sendEmailVerification,
    updatePassword,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signInWithCredential
} from 'firebase/auth';
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    onSnapshot,
    serverTimestamp,
    deleteField,
    arrayUnion,
    arrayRemove,
    query,
    collection,
    where,
    getDocs
} from 'firebase/firestore';
import { User } from '../types';
import { useNavigate } from 'react-router-dom';
import NotificationService from '../services/NotificationService';
import { checkLivreurShouldBeOnline } from '../utils/dateUtils';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { PushNotifications } from '@capacitor/push-notifications';

interface AuthContextType {
    currentUser: User | null;
    loading: boolean;
    login: (email: string, pass: string, requestedRole: string) => Promise<void>;
    register: (name: string, email: string, pass: string, role: 'client' | 'restaurateur' | 'livreur', restaurantName?: string, phone?: string, wilaya?: string) => Promise<{ success: boolean; message?: string }>;
    loginWithGoogle: (role: 'client' | 'restaurateur' | 'livreur', intent: 'login' | 'register') => Promise<void>;
    logout: () => void;
    refreshProfile: () => Promise<void>;
    checkRestaurantStatus: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    updateUserPassword: (password: string) => Promise<void>;
    updateUserProfile: (updates: Partial<User>) => Promise<void>;
    isPasswordRecovery: boolean;
    profileLoaded: boolean;
    authError: string | null;
    requestNotificationPermission: () => Promise<CustomNotificationPermission>;
    notificationPermission: CustomNotificationPermission;
    pendingNotification: any;
    clearPendingNotification: () => void;
    devCreateTestAccount: (email: string, name: string, role: 'client' | 'restaurateur' | 'livreur' | 'super_admin') => Promise<void>;
    pendingRoleMismatch: { role: Role; message: string } | null;
    clearRoleMismatch: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(() => {
        try {
            const cached = localStorage.getItem('yumii_user_cache');
            if (!cached) return null;
            const user = JSON.parse(cached);
            if (!user || !user.role) return null;
            
            // IMPORTANT: Mark cached user as role NOT fetched yet
            // This forces MainApp to wait on splash screen until Firestore confirms the role
            return { ...user, roleFetched: false };
        } catch { return null; }
    });

    const [loading, setLoading] = useState(true); // Always start as true to avoid initial UI flash

    const [profileLoaded, setProfileLoaded] = useState(false);
    const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
    const userRef = useRef<User | null>(null);
    const isRegistering = useRef(false);
    const fetchInProgressRef = useRef(false);
    const pendingTokenRef = useRef<string | null>(null);
    const [pendingNotification, setPendingNotification] = useState<any>(null);

    const clearPendingNotification = useCallback(() => {
        setPendingNotification(null);
    }, []);
    const ROLE_STORAGE_KEY = 'yumii_auth_role';
    const ADMIN_EMAILS = ['amine@gmail.com'];
    const [notificationPermission, setNotificationPermission] = useState<CustomNotificationPermission>(() => {
        if (!Capacitor.isNativePlatform()) {
            try {
                if (typeof window !== 'undefined' && 'Notification' in window && (window as any).Notification) {
                    return (window as any).Notification.permission;
                }
            } catch (e) {}
        }
        return 'default'; // Native check happens in useEffect
    });

    const checkNotificationPermission = useCallback(async () => {
        if (!Capacitor.isNativePlatform()) {
            if (typeof window !== 'undefined' && 'Notification' in window) {
                setNotificationPermission((window.Notification as any).permission);
            }
            return;
        }

        try {
            const result = await PushNotifications.checkPermissions();
            const permission: CustomNotificationPermission = 
                result.receive === 'granted' ? 'granted' : 
                result.receive === 'prompt' ? 'default' : 'denied';
            
            setNotificationPermission(permission);
            console.log("📜 Native Notification permission check:", permission);

            // On Native, we MUST create a channel for sound/vibration on Android 8+
            // Set to max importance (5) and visibility (1) to ensure it can potentially bypass DND
            if (Capacitor.getPlatform() === 'android') {
                try {
                    await PushNotifications.createChannel({
                        id: 'yumii_alerts_v2',
                        name: 'Yumii Alerts',
                        description: 'Alarmes critiques pour les commandes et missions (Peuvent ignorer le mode silencieux si activé)',
                        importance: 5, // IMPORTANCE_HIGH
                        visibility: 1, // VISIBILITY_PUBLIC
                        sound: 'restaurant_alert',
                        vibration: true
                    });
                    console.log("✅ [FCM] Notification channel v2 configured with MAX priority");
                } catch (e) {
                    console.error("❌ [FCM] Error creating channel:", e);
                }
            }

            // AUTO-REGISTER if granted
            if (permission === 'granted') {
                console.log("📡 Native Notification Permission: GRANTED -> Auto-registering...");
                await PushNotifications.register();
            } else if (permission === 'default') {
                console.log("📡 Native Notification Permission: READY TO PROMPT");
            } else {
                console.warn("📡 Native Notification Permission: DENIED");
            }
        } catch (error) {
            console.error("❌ Error checking native permissions:", error);
        }
    }, []);

    useEffect(() => {
        checkNotificationPermission();

        // Re-check on App resume (e.g. user manually enabled permissions in settings)
        if (Capacitor.isNativePlatform()) {
            const listener = App.addListener('appStateChange', ({ isActive }) => {
                if (isActive) {
                    console.log("🔄 App back to foreground -> sync notifications state");
                    checkNotificationPermission();
                }
            });
            return () => {
                listener.then(l => l.remove());
            };
        }
    }, [checkNotificationPermission]);

    useEffect(() => {
        userRef.current = currentUser;
        if (currentUser) {
            localStorage.setItem('yumii_user_cache', JSON.stringify(currentUser));
        }
    }, [currentUser]);

    const currentStatusRef = useRef(currentUser?.restaurantStatus);
    useEffect(() => {
        currentStatusRef.current = currentUser?.restaurantStatus;
    }, [currentUser?.restaurantStatus]);

    // Firestore Realtime Listener for Restaurant Status
    useEffect(() => {
        if (!currentUser || currentUser.role !== 'restaurateur' || !currentUser.restaurantId) return;

        console.log("📡 Subscribing to Firestore restaurant updates:", currentUser.restaurantId);
        const unsub = onSnapshot(doc(db, 'restaurants', currentUser.restaurantId.trim()), (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                const rawStatus = (data.status || 'pending').toLowerCase();
                const newStatus = (rawStatus === 'active' || rawStatus === 'approved') ? 'approved' : rawStatus;

                if (currentStatusRef.current !== newStatus) {
                    setCurrentUser(prev => prev ? { ...prev, restaurantStatus: newStatus as any } : null);
                    if (currentUser.uid) {
                        updateDoc(doc(db, 'users', currentUser.uid), { restaurantStatus: newStatus }).catch(console.error);
                    }
                    currentStatusRef.current = newStatus as any;
                }
            }
        });

        return () => unsub();
    }, [currentUser?.restaurantId, currentUser?.role]);

    // Firestore Realtime Listener for Livreur Status
    useEffect(() => {
        if (!currentUser || currentUser.role !== 'livreur' || !currentUser.uid) return;

        console.log("📡 Subscribing to Firestore livreur updates:", currentUser.uid);
        const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                const rawStatus = (data.livreurStatus || data.status || 'pending').toLowerCase();
                const newStatus = (rawStatus === 'active' || rawStatus === 'approved') ? 'approved' : rawStatus;

                setCurrentUser(prev => {
                    if (!prev) return null;
                    const locationChanged = JSON.stringify(prev.location) !== JSON.stringify(data.location);
                    
                    const profileChanged = 
                        prev.livreurStatus !== newStatus || 
                        prev.isOnline !== data.isOnline || 
                        locationChanged ||
                        prev.rating !== data.rating ||
                        prev.ratingCount !== (data.ratingCount ?? data.rating_count) ||
                        prev.restaurantRating !== data.restaurantRating ||
                        prev.deliveredOrdersCount !== data.deliveredOrdersCount;

                    if (profileChanged) {
                        return { 
                            ...prev, 
                            livreurStatus: newStatus as any, 
                            isOnline: data.isOnline ?? false,
                            location: data.location,
                            rating: data.rating !== undefined ? data.rating : prev.rating,
                            ratingCount: data.ratingCount !== undefined ? data.ratingCount : (data.rating_count !== undefined ? data.rating_count : prev.ratingCount),
                            restaurantRating: data.restaurantRating !== undefined ? data.restaurantRating : prev.restaurantRating,
                            restaurantRatingCount: data.restaurantRatingCount !== undefined ? data.restaurantRatingCount : prev.restaurantRatingCount,
                            deliveredOrdersCount: data.deliveredOrdersCount !== undefined ? data.deliveredOrdersCount : prev.deliveredOrdersCount
                        };
                    }
                    return prev;
                });
            }
        });

        return () => unsub();
    }, [currentUser?.uid, currentUser?.role]);

    // FCM Token Auto-Sync on load/login to handle domain changes
    useEffect(() => {
        if (currentUser && messaging && !Capacitor.isNativePlatform() && typeof window !== 'undefined' && 'Notification' in window && (window.Notification as any).permission === 'granted') {
            const syncToken = async () => {
                try {
                    const registration = await navigator.serviceWorker.ready;
                    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
                    const token = await getToken(messaging!, {
                        vapidKey,
                        serviceWorkerRegistration: registration
                    });
                    
                    if (token && token !== currentUser.fcmToken) {
                        console.log("🔄 FCM Token synced for domain:", window.location.hostname);
                        localStorage.setItem('my_fcm_token', token);
                        // Using setDoc with merge is more robust than updateDoc for new accounts
                        await setDoc(doc(db, 'users', currentUser.uid), { 
                            fcmToken: token,
                            fcmTokens: arrayUnion(token),
                            lastTokenSync: serverTimestamp()
                        }, { merge: true });
                        setCurrentUser(prev => prev ? { ...prev, fcmToken: token } : null);
                    }
                } catch (err) {
                    console.error("❌ FCM auto-sync error:", err);
                }
            };
            syncToken();
        }
    }, [currentUser?.uid, notificationPermission]);

    const fetchProfile = useCallback(async (firebaseUser: any) => {
        if (isRegistering.current) {
            console.log("⏭️ [Auth] Registration in progress, skipping fetchProfile");
            return;
        }

        console.log("📥 [Auth] fetchProfile starting for:", firebaseUser.email, "UID:", firebaseUser.uid);
        fetchInProgressRef.current = true;
        setProfileLoaded(false);

        try {
            const uid = firebaseUser.uid;
            const profileDoc = await getDoc(doc(db, 'users', uid));
            
            console.log("📄 [Auth] Firestore Doc Result:", profileDoc.exists() ? "Exists" : "MISSING!");
            let profileData = profileDoc.exists() ? profileDoc.data() : null;

            // [STRICT ROLE CHECK V3] Intercept auth before state is updated to prevent ghost sessions
            const savedRole = localStorage.getItem(ROLE_STORAGE_KEY);
            if (savedRole && profileData?.role && profileData.role !== savedRole) {
                console.error(`❌ Role mismatch blocked in context! App expected ${savedRole} but fetched ${profileData.role}`);
                const actualRoleDisplay = profileData.role === 'restaurateur' ? 'Restaurant' : profileData.role === 'livreur' ? 'Livreur' : 'Client';
                const correctApp = actualRoleDisplay === 'Client' ? "l'application Client" : "l'application Pro";
                
                await signOut(auth);
                localStorage.removeItem(ROLE_STORAGE_KEY);
                
                const mismatchMessage = `Ce compte appartient à un espace ${actualRoleDisplay}.`;
                setPendingRoleMismatch({ 
                    role: profileData.role as Role, 
                    message: mismatchMessage 
                });
                
                setAuthError(null); // Clear generic error if we have a specific mismatch
                setCurrentUser(null);
                setProfileLoaded(true);
                setLoading(false);
                return;
            }

            if (profileData) {
                console.log("🔑 [Auth] Firestore Data found:", {
                    role: profileData.role,
                    name: profileData.name,
                    email: profileData.email,
                    livreurStatus: profileData.livreurStatus || profileData.status
                });
            } else if (!profileDoc.exists()) {
                console.warn("⚠️ [Auth] NO FIRESTORE PROFILE FOUND for UID:", uid);
                
                // AUTO-RECOVERY LOGIC
                const isAdminEmail = ADMIN_EMAILS.includes(firebaseUser.email?.toLowerCase() || '');
                const defaultRole = isAdminEmail ? 'super_admin' : 'client';
                
                console.log(`🔧 [Auth] Auto-creating missing profile for ${firebaseUser.email} as ${defaultRole}`);
                
                profileData = {
                    uid: uid,
                    name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Utilisateur',
                    email: firebaseUser.email,
                    role: defaultRole,
                    createdAt: serverTimestamp(),
                    needsProfileCompletion: true,
                    registrationRecovered: true
                };

                // Save to Firestore so it exists for next time
                await setDoc(doc(db, 'users', uid), profileData);
                console.log("✅ [Auth] Missing profile recovered and saved.");
            }

            const rawStatus = profileData?.restaurantStatus || 'pending';
            const normalizedStatus = (rawStatus === 'active' || rawStatus === 'approved') ? 'approved' : rawStatus;

            const isAdminEmail = ADMIN_EMAILS.includes(firebaseUser.email?.toLowerCase() || '');
            let role = profileData?.role || null;
            
            if (!profileData?.role && !isAdminEmail) {
                console.log("⚡ [Auth] Role missing in Firestore for non-admin user.");
            }
            
            if (isAdminEmail) {
                role = 'super_admin';
                // Retro-fix in Firestore if needed
                if (profileData && profileData.role !== 'super_admin') {
                    console.log("⚡ Auto-elevating user to super_admin in Firestore:", firebaseUser.email);
                    updateDoc(doc(db, 'users', uid), { role: 'super_admin' }).catch(console.error);
                }
            }

            let isOnline = profileData?.isOnline !== undefined ? profileData.isOnline : false;
            
            // [NEW] AUTO-ONLINE FOR LIVREURS
            // If it's a livreur, check if they are currently in a shift.
            // If yes, and they are currently OFFLINE, we auto-toggle them ONLINE once at startup.
            if (role === 'livreur' && !isOnline && profileData?.availabilitySchedule) {
                const shouldBeOnline = checkLivreurShouldBeOnline(profileData.availabilitySchedule);
                if (shouldBeOnline) {
                    console.log("🛵 [Auth] Driver shift detected! Auto-toggling isOnline to true.");
                    isOnline = true;
                    // Sync to Firestore immediately
                    updateDoc(doc(db, 'users', uid), { isOnline: true }).catch(err => console.error("❌ Auto-online sync failed:", err));
                }
            }

            const finalUser: User = {
                uid: uid,
                email: firebaseUser.email || '',
                name: profileData?.name || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Utilisateur',
                role: role as any,
                restaurantId: profileData?.restaurantId ? String(profileData.restaurantId).trim() : null,
                restaurantStatus: normalizedStatus,
                phone: profileData?.phone || '',
                wilaya: profileData?.wilaya || '',
                isOnline: isOnline,
                rating: profileData?.rating !== undefined ? profileData.rating : 0,
                ratingCount: profileData?.ratingCount !== undefined ? profileData.ratingCount : (profileData?.rating_count !== undefined ? profileData.rating_count : 0),
                restaurantRating: profileData?.restaurantRating !== undefined ? profileData.restaurantRating : 0,
                restaurantRatingCount: profileData?.restaurantRatingCount !== undefined ? profileData.restaurantRatingCount : 0,
                deliveredOrdersCount: profileData?.deliveredOrdersCount || 0,
                fcmToken: profileData?.fcmToken || null,
                livreurStatus: role === 'livreur' ? (profileData?.livreurStatus || profileData?.status || 'pending') : undefined,
                roleFetched: true,
                needsProfileCompletion: profileData?.needsProfileCompletion || false,
                hasBeenWelcomed: profileData?.hasBeenWelcomed || false
            };

            console.log("👤 [DEBUG] Profile Ready to set:", {
                uid,
                email: finalUser.email,
                role: finalUser.role,
                roleFetched: finalUser.roleFetched
            });

            // Force update if role is different or if it was not fetched before
            const needsUpdate = !userRef.current || 
                                userRef.current.role !== finalUser.role || 
                                !userRef.current.roleFetched ||
                                JSON.stringify(finalUser) !== JSON.stringify(userRef.current);

            if (needsUpdate) {
                console.log("🔄 [Auth] Updating currentUser state with fresh profile");
                setCurrentUser(finalUser);
            } else {
                console.log("✅ [Auth] Profile identical to current state, skipping update");
            }

            // [NEW] Sync pending token if exists
            if (pendingTokenRef.current) {
                console.log("🔄 [FCM] Syncing pending token for newly fetched profile:", uid);
                // Ensure auth state is ready for the rules check
                if (auth.currentUser?.uid === uid) {
                    localStorage.setItem('my_fcm_token', pendingTokenRef.current);
                    setDoc(doc(db, 'users', uid), { 
                        fcmToken: pendingTokenRef.current,
                        fcmTokens: arrayUnion(pendingTokenRef.current),
                        lastTokenSync: serverTimestamp() 
                    }, { merge: true }).then(() => {
                        console.log("✅ [FCM] Pending token synced successfully");
                        pendingTokenRef.current = null;
                    }).catch(err => console.error("❌ [FCM] Pending token sync failed:", err));
                }
            }

            setProfileLoaded(true);
        } catch (error) {
            console.error("Error fetching Firebase profile:", error);
        } finally {
            fetchInProgressRef.current = false;
            setLoading(false); // On s'assure que loading passe à false une fois le profil chargé
        }
    }, []);

    // Handle Google Redirect Result
    const handleGoogleProfile = async (user: any, requestedRole?: 'client' | 'restaurateur' | 'livreur') => {
        try {
            const profileDoc = await getDoc(doc(db, 'users', user.uid));
            if (!profileDoc.exists()) {
                console.log("🆕 Creating new Google profile for:", user.email, "with role:", requestedRole);
                const isAdminEmail = ADMIN_EMAILS.includes(user.email?.toLowerCase() || '');
                const finalRole = isAdminEmail ? 'super_admin' : (requestedRole || 'client');
                let restaurantId = null;
                
                if (finalRole === 'restaurateur') {
                    restaurantId = `resto_${Date.now()}`;
                }

                const profileData: any = {
                    uid: user.uid,
                    name: user.displayName || user.email?.split('@')[0] || 'Utilisateur',
                    email: user.email,
                    role: finalRole,
                    restaurantId: restaurantId,
                    phone: '', // Google doesn't provide this
                    wilaya: '', // Google doesn't provide this
                    createdAt: serverTimestamp(),
                    needsProfileCompletion: true // Flag for Strict Flow
                };

                if (finalRole === 'restaurateur') profileData.restaurantStatus = 'incomplete';
                if (finalRole === 'livreur') {
                    profileData.isOnline = false;
                    profileData.livreurStatus = 'pending';
                }

                await setDoc(doc(db, 'users', user.uid), profileData);
                
                if (finalRole === 'restaurateur') {
                    await setDoc(doc(db, 'restaurants', restaurantId!), {
                        id: restaurantId,
                        ownerId: user.uid,
                        ownerName: profileData.name,
                        name: `${profileData.name}'s Restaurant`,
                        status: 'incomplete',
                        phone: '',
                        wilaya: '',
                        createdAt: serverTimestamp(),
                        image: "https://images.unsplash.com/photo-1614332287897-cdc485fa562d?q=80&w=2070&auto=format&fit=crop",
                        rating: 0,
                        ratingCount: 0
                    });
                }
            } else {
                console.log("✅ Google profile already exists for:", user.email);
                // [NEW] Role check for existing users in handleGoogleProfile (important for Redirect)
                const existingRole = profileDoc.data()?.role;
                if (requestedRole && existingRole && existingRole !== requestedRole) {
                    await signOut(auth);
                    const actualRoleDisplay = existingRole === 'restaurateur' ? 'Restaurant' : existingRole === 'livreur' ? 'Livreur' : 'Client';
                    setPendingRoleMismatch({
                        role: existingRole as Role,
                        message: `Ce compte Google appartient à un profil ${actualRoleDisplay}.`
                    });
                    throw new Error(`wrong_role_login:${existingRole}`);
                }
            }
            await fetchProfile(user);
        } catch (error) {
            console.error("❌ Error in handleGoogleProfile:", error);
            throw error;
        }
    };

    // Handle Google Redirect Result
    useEffect(() => {
        const handleRedirect = async () => {
            try {
                const result = await getRedirectResult(auth);
                if (result) {
                    const user = result.user;
                    console.log("✅ Google Redirect Success:", user.email);
                    const savedRole = localStorage.getItem(ROLE_STORAGE_KEY) as any;
                    await handleGoogleProfile(user, savedRole);
                    localStorage.removeItem(ROLE_STORAGE_KEY);
                }
            } catch (error: any) {
                console.error("❌ Google Redirect Error:", error);
                const isNoAuthEvent = error.code === 'auth/no-auth-event';
                const isMissingInitialState = error.message?.includes('missing initial state');
                const isRoleMismatch = error.message?.includes('wrong_role');
                
                if (!isNoAuthEvent && !isMissingInitialState && !isRoleMismatch) {
                    setAuthError(`Erreur de connexion Google (Redirect): ${error.message}`);
                }
            }
        };

        if (!Capacitor.isNativePlatform()) {
            handleRedirect();
        }
    }, [fetchProfile, handleGoogleProfile]);

    const [authError, setAuthError] = useState<string | null>(null);
    const [pendingRoleMismatch, setPendingRoleMismatch] = useState<{ role: Role; message: string } | null>(null);

    const clearRoleMismatch = () => setPendingRoleMismatch(null);

    const requestNotificationPermission = useCallback(async (): Promise<CustomNotificationPermission> => {
        console.log("🔍 requestNotificationPermission called. Native Platform:", Capacitor.isNativePlatform());

        try {
            let permission: CustomNotificationPermission = 'default';

            if (Capacitor.isNativePlatform()) {
                console.log("🔔 [NATIVE] Checking permissions...");
                
                // 1. Ensure channel exists (Mandatory for Android 8+)
                if (Capacitor.getPlatform() === 'android') {
                    try {
                        await PushNotifications.createChannel({
                            id: 'yumii_alerts_v2',
                            name: 'Yumii Alerts',
                            description: 'Alarmes critiques pour les commandes et missions',
                            importance: 5,
                            visibility: 1,
                            sound: 'restaurant_alert',
                            vibration: true,
                        });
                        console.log("📺 [ANDROID] Channel verified");
                    } catch (e) {
                        console.error("❌ [ANDROID] Channel creation failed", e);
                    }
                }

                const check = await PushNotifications.checkPermissions();
                console.log("🔍 [NATIVE] Current state:", check.receive);
                
                if (check.receive === 'granted') {
                    permission = 'granted';
                } else {
                    console.log("🚀 [NATIVE] Requesting permission popup...");
                    const result = await PushNotifications.requestPermissions();
                    console.log("📊 [NATIVE] Request result:", result.receive);
                    permission = result.receive === 'granted' ? 'granted' : 'denied';
                }
            } else {
                if (!messaging) {
                    console.warn("⚠️ Messaging not initialized");
                    return 'default';
                }
                console.log("🔔 [WEB] Requesting permission...");
                if (typeof window !== 'undefined' && 'Notification' in window) {
                    permission = await (window.Notification as any).requestPermission();
                } else {
                    permission = 'default';
                }
            }

            setNotificationPermission(permission);
            console.log("📜 Permission set in state:", permission);

            if (permission === 'granted') {
                if (Capacitor.isNativePlatform()) {
                    console.log("📡 Registering for native push...");
                    try {
                        await PushNotifications.register();
                        console.log("✅ PushNotifications.register() called");
                    } catch (e) {
                        console.error("❌ PushNotifications.register() failed:", e);
                    }
                    return 'granted'; // Token will be caught by the listener
                }

                if (!messaging) return 'granted';
                console.log("⏳ Waiting for Service Worker to be ready...");
                const registration = await navigator.serviceWorker.ready;
                console.log("✅ Service Worker ready:", registration.active?.state);

                const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
                console.log("🔑 VAPID Key found:", !!vapidKey);

                console.log("📡 Calling getToken...");
                const token = await getToken(messaging, {
                    vapidKey,
                    serviceWorkerRegistration: registration
                });
                if (token) {
                    console.log("📦 FCM Token generated successfully:", token);
                    if (userRef.current?.uid) {
                        localStorage.setItem('my_fcm_token', token);
                        await updateDoc(doc(db, 'users', userRef.current.uid), { fcmToken: token, fcmTokens: arrayUnion(token) });
                        setCurrentUser(prev => prev ? { ...prev, fcmToken: token } : null);
                        console.log("💾 FCM Token saved to Firestore");
                    }
                } else {
                    console.warn("⚠️ No FCM token received (getToken returned null/empty)");
                }
            } else {
                console.warn("🚫 Notification permission denied or dismissed:", permission);
            }
            return permission;
        } catch (error: any) {
            console.error("❌ Fatal Error in requestNotificationPermission:", error);
            console.error("🥞 Stack Trace:", error?.stack);
            return 'default';
        }
    }, []);

    // 2. Auth State Listener
    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            PushNotifications.addListener('registration', (token) => {
                console.log("📦 [FCM] Native Token generated:", token.value);
                const uid = userRef.current?.uid;
                
                if (uid) {
                    console.log("💾 [FCM] Saving token to Firestore for user:", uid);
                    const saveToken = async (attempt = 1) => {
                        try {
                            // Verify target matches current logged in user to pass security rules
                            if (auth.currentUser?.uid !== uid) {
                                console.warn(`⚠️ [FCM] Auth state mismatch (Current: ${auth.currentUser?.uid}, Ref: ${uid}). Postponing.`);
                                pendingTokenRef.current = token.value;
                                return;
                            }

                            localStorage.setItem('my_fcm_token', token.value);
                            await setDoc(doc(db, 'users', uid), { 
                                fcmToken: token.value,
                                fcmTokens: arrayUnion(token.value),
                                lastTokenSync: serverTimestamp() 
                            }, { merge: true });
                            console.log("✅ [FCM] Token saved successfully");
                            setCurrentUser(prev => prev ? { ...prev, fcmToken: token.value } : null);
                        } catch (err: any) {
                            if (err.code === 'permission-denied' && attempt < 3) {
                                console.warn(`⏳ [FCM] Permission denied, retrying in ${attempt * 1000}ms...`);
                                setTimeout(() => saveToken(attempt + 1), attempt * 1000);
                            } else {
                                console.error("❌ [FCM] Error saving token:", err);
                            }
                        }
                    };
                    saveToken();
                } else {
                    console.warn("⚠️ [FCM] Token generated but no currentUser yet. Storing for later sync.");
                    pendingTokenRef.current = token.value;
                }
            });

            PushNotifications.addListener('registrationError', (error) => {
                console.error("❌ Native Push Registration Error:", error);
            });
            
            PushNotifications.addListener('pushNotificationReceived', (notification) => {
                console.log("🔔 Native Push Received (Foreground):", notification);
                
                const data = notification.data || {};
                const title = notification.title || "Yumii";
                const body = notification.body || "";

                const detail = {
                    title,
                    message: body,
                    type: data.type || (title.toLowerCase().includes('🍔') || title.toLowerCase().includes('nouvelle commande') ? 'new_order' : 
                          (title.toLowerCase().includes('🛵') || title.toLowerCase().includes('mission') ? 'new_mission' : 'info')),
                    role: data.role,
                    orderId: data.orderId,
                    restaurantId: data.restaurantId,
                    uid: data.uid,
                    data: data
                };

                // Dispatch to wake up NotificationContext and trigger Alarm
                window.dispatchEvent(new CustomEvent('yumii:notification', { detail }));
            });
            
            PushNotifications.addListener('pushNotificationActionPerformed', (notification: any) => {
                console.log("🖱️ Native Push Action Performed:", notification);
                // Capacitor v5 sometimes nests data under notification.notification, sometimes direct
                const data = notification.data || notification.notification?.data || {};
                const title = notification.notification?.title || notification.title || "Yumii";
                const body = notification.notification?.body || notification.body || "";
                
                // CRITICAL: Synchronous signal for MainApp.tsx
                (window as any).lastYumiiPushTime = Date.now();

                const detail = {
                    title: title,
                    body: body,
                    type: data.type || (title.toLowerCase().includes('relance') ? 'nudge' : 'info'),
                    role: data.role,
                    orderId: data.orderId,
                    uid: data.uid
                };

                // Dispatch standardized event for the MainApp.tsx router
                window.dispatchEvent(new CustomEvent('yumii:notification', { detail }));
                
                // CRITICAL: Persistent state for cold starts
                setPendingNotification(detail);
            });
        }

        console.log("🔌 Initializing PWA message listener & onAuthStateChanged");
        
        // Dedicated Google Redirect result handler is handled above (handleRedirect)
        // Removing redundant/harmful handler that was setting role-less users

        if (!Capacitor.isNativePlatform() && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', (event) => {
                if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
                    console.log("💌 [PWA] Notification Click Event received from Service Worker:", event.data.payload);
                    const data = event.data.payload;
                    const detail = {
                        title: data.title || "Yumii",
                        message: data.body || "",
                        type: data.type || (data.title?.toLowerCase().includes('relance') ? 'nudge' : 'info'),
                        role: data.role,
                        orderId: data.orderId,
                        uid: data.uid
                    };

                    (window as any).lastYumiiPushTime = Date.now();
                    window.dispatchEvent(new CustomEvent('yumii:notification', { detail }));
                    setPendingNotification(detail);
                }
            });
        }

        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            console.log("👤 Auth State Changed Event:", firebaseUser ? `USER: ${firebaseUser.email}` : "USER: null");
            if (firebaseUser) {
                console.log("📥 Fetching profile for authenticated user...", { verified: firebaseUser.emailVerified });

                // Initialize notifications silently if already granted
                if (messaging && !Capacitor.isNativePlatform() && typeof window !== 'undefined' && 'Notification' in window && (window.Notification as any).permission === 'granted') {
                    const registration = await navigator.serviceWorker.ready;
                    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
                    getToken(messaging, {
                        vapidKey,
                        serviceWorkerRegistration: registration
                    }).then((token) => {
                        if (token) {
                            console.log("📦 FCM Token refreshed");
                            localStorage.setItem('my_fcm_token', token);
                            updateDoc(doc(db, 'users', firebaseUser.uid), { fcmToken: token, fcmTokens: arrayUnion(token) }).catch(console.error);
                        }
                    }).catch(console.error);
                }

                if (messaging) {
                    onMessage(messaging, (payload: any) => {
                        console.log("🔔 Foreground notification received:", payload);
                        const title = payload.notification?.title || payload.data?.title || "Yumii";
                        const body = payload.notification?.body || payload.data?.body || "";
                        const data = payload.data || {};

                        const detail = {
                            title,
                            message: body,
                            type: data.type || (title.toLowerCase().includes('🍔') || title.toLowerCase().includes('nouvelle commande') ? 'new_order' : 
                                  (title.toLowerCase().includes('🛵') || title.toLowerCase().includes('mission') ? 'new_mission' : 'info')),
                            role: data.role,
                            orderId: data.orderId,
                            restaurantId: data.restaurantId,
                            uid: data.uid,
                            data: data // Ensure data object is passed for NotificationContext.tsx filters
                        };

                        // Dispatch standardized event for the MainApp.tsx router
                        window.dispatchEvent(new CustomEvent('yumii:notification', { detail }));
                        
                        // Persistent state for handling
                        setPendingNotification(detail);
                    });
                }
                
                const isGoogle = firebaseUser.providerData.some(p => p.providerId === 'google.com');
                if (!isGoogle && !firebaseUser.emailVerified) {
                    console.warn("⚠️ Email not verified! User might be blocked.");
                }

                await fetchProfile(firebaseUser as any);
            } else {
                console.log("🚪 No user session found by listener.");
                setCurrentUser(null);
                setProfileLoaded(false);
                setLoading(false);
            }
            console.log("🔓 Flow finished: setting loading to false");
            setLoading(false);
        });
        return () => unsubscribe();
    }, [fetchProfile]);

    const devCreateTestAccount = async (email: string, name: string, role: 'client' | 'restaurateur' | 'livreur' | 'super_admin') => {
        if (!import.meta.env.DEV) return;
        try {
            const pass = "password123";
            console.log(`🛠️ [DEV] Creating test account: ${email} (${role})`);
            
            // 1. Create in Auth Emulator
            const { user } = await createUserWithEmailAndPassword(auth, email, pass);
            
            // 2. Create Profile in Firestore
            let restaurantId = null;
            if (role === 'restaurateur') {
                restaurantId = `dev_resto_${Date.now()}`;
            }

            const profileData: any = {
                uid: user.uid,
                name: name,
                email: email,
                role: role,
                restaurantId: restaurantId,
                phone: "0550000000",
                wilaya: "Alger",
                createdAt: serverTimestamp()
            };

            if (role === 'restaurateur') profileData.restaurantStatus = 'approved';
            if (role === 'livreur') profileData.livreurStatus = 'approved';

            await setDoc(doc(db, 'users', user.uid), profileData);

            if (role === 'restaurateur') {
                await setDoc(doc(db, 'restaurants', restaurantId!), {
                    id: restaurantId,
                    ownerId: user.uid,
                    ownerName: name, 
                    name: `${name}'s Gourmet`,
                    status: 'approved',
                    wilaya: 'Alger',
                    createdAt: serverTimestamp(),
                    image: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=800",
                    rating: 5,
                    ratingCount: 1,
                    subscriptionStatus: 'active',
                    subscriptionType: 'mensuel'
                });
            }
            
            window.location.reload(); // Refresh to catch all new states
        } catch (err: any) {
            console.error("❌ Test account creation failed:", err);
            if (err.code === 'auth/email-already-in-use') {
                alert("Ce compte existe déjà. Connectez-vous avec 'password123'.");
            }
        }
    };

    // Always check permissions on Native
    useEffect(() => {
        // Automatically request if we have a user and permission is not granted
        if (Capacitor.isNativePlatform() && currentUser && notificationPermission !== 'granted' && notificationPermission !== 'denied') {
            const hasIgnored = localStorage.getItem('yumii_notif_ignored');
            if (!hasIgnored) {
                console.log("🛡️ Requesting Push Permissions silently...");
                requestNotificationPermission();
            }
        }
    }, [currentUser?.uid, notificationPermission]);

    const login = async (email: string, pass: string, requestedRole: string) => {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', email.toLowerCase().trim()));
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
            throw new Error('not_registered');
        }
        
        const userData = snapshot.docs[0].data();
        if (userData.role !== requestedRole) {
            throw new Error(`wrong_role_login:${userData.role}`);
        }

        localStorage.setItem(ROLE_STORAGE_KEY, requestedRole);
        await signInWithEmailAndPassword(auth, email, pass);
    };

    const register = async (name: string, email: string, pass: string, role: 'client' | 'restaurateur' | 'livreur', restaurantName?: string, phone?: string, wilaya?: string) => {
        isRegistering.current = true;
        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('email', '==', email.toLowerCase().trim()));
            const snapshot = await getDocs(q);
            
            if (!snapshot.empty) {
                const userData = snapshot.docs[0].data();
                if (userData.role !== role) {
                    throw new Error(`wrong_role_register:${userData.role}`);
                } else {
                    localStorage.setItem(ROLE_STORAGE_KEY, role);
                    await signInWithEmailAndPassword(auth, email, pass);
                    return { success: true, message: "Vous étiez déjà inscrit. Connexion réussie." };
                }
            }

            localStorage.setItem(ROLE_STORAGE_KEY, role);
            const { user } = await createUserWithEmailAndPassword(auth, email, pass);

            try {
                let restaurantId = null;
                if (role === 'restaurateur') {
                    restaurantId = `resto_${Date.now()}`;
                }

                const profileData: any = {
                    uid: user.uid,
                    name: name,
                    email: email,
                    role: role,
                    restaurantId: restaurantId,
                    phone: phone || '',
                    wilaya: wilaya || '',
                    createdAt: serverTimestamp()
                };

                if (role === 'restaurateur') {
                    profileData.restaurantStatus = 'pending';
                    profileData.restaurantName = restaurantName || '';
                }
                if (role === 'livreur') {
                    profileData.isOnline = false;
                    profileData.livreurStatus = 'incomplete';
                    profileData.deliveredOrdersCount = 0;
                    profileData.rating = 0;
                    profileData.ratingCount = 0;
                    profileData.restaurantRating = 0;
                    profileData.restaurantRatingCount = 0;
                }

                await setDoc(doc(db, 'users', user.uid), profileData);

                if (role === 'restaurateur') {
                    const trialExpiry = new Date();
                    trialExpiry.setDate(trialExpiry.getDate() + 7);

                    await setDoc(doc(db, 'restaurants', restaurantId!), {
                        id: restaurantId,
                        ownerId: user.uid,
                        ownerName: name, 
                        name: restaurantName || `${name}'s Restaurant`,
                        status: 'pending',
                        phone: phone || '', 
                        wilaya: wilaya || '', 
                        createdAt: serverTimestamp(),
                        image: "https://images.unsplash.com/photo-1614332287897-cdc485fa562d?q=80&w=2070&auto=format&fit=crop",
                        rating: 0,
                        ratingCount: 0,
                        subscriptionType: 'trial',
                        subscriptionStatus: 'trial',
                        trialExpiry: trialExpiry
                    });
                }

                await sendEmailVerification(user);
                await signOut(auth); // Crucial: on réinitialise la session pour forcer le login après validation

                return {
                    success: true,
                    message: "Un email de confirmation a été envoyé. Veuillez vérifier votre boîte de réception (et vos spams)."
                };
            } catch (firestoreError: any) {
                console.error("Firestore creation failed, cleaning up Auth user...", firestoreError);
                // Si la création Firestore échoue, on supprime l'utilisateur Auth pour permettre de recommencer proprement
                try {
                    await user.delete();
                } catch (deleteError) {
                    console.error("Failed to cleanup Auth user after Firestore error", deleteError);
                }
                throw firestoreError;
            }
        } catch (error: any) {
            console.error("Registration fatal error:", error);
            throw new Error(error.message);
        } finally {
            setTimeout(() => { isRegistering.current = false; }, 2000);
        }
    };

    const loginWithGoogle = async (role: 'client' | 'restaurateur' | 'livreur', intent: 'login' | 'register') => {
        console.log("🚀 loginWithGoogle called [CLEAN RECOVERY V3] with role:", role, "intent:", intent);
        setAuthError(null);
        setLoading(true);

        if (role) {
            localStorage.setItem(ROLE_STORAGE_KEY, role);
        }

        try {
            let firebaseUser;

            if (Capacitor.isNativePlatform()) {
                console.log("📱 [Native Auth] Attempting Native Sign-In...");
                try {
                    const result = await FirebaseAuthentication.signInWithGoogle({
                        clientId: '798074967750-e7o9d0ssegs2snd3rsu2bru3mi19hcst.apps.googleusercontent.com'
                    });
                    
                    if (result.credential?.idToken) {
                        const credential = GoogleAuthProvider.credential(result.credential.idToken);
                        const userCredential = await signInWithCredential(auth, credential);
                        firebaseUser = userCredential.user;
                        console.log("✅ [Native Auth] Success! User:", firebaseUser.email);
                    } else {
                        throw new Error("No ID token returned from Google Sign-In");
                    }
                } catch (e: any) {
                    console.error("❌ [Native Auth] Failed. This is often due to missing Play Store SHA-1 in Firebase. Error:", e);
                    // Do not fallback to redirect on Native Android as it often throws auth/argument-error
                    throw new Error(`Erreur Native Google (Vérifiez les clés SHA-1) : ${e.message}`);
                }
            } else {
                console.log("🌐 Running on Web/PWA, using standard Firebase Web Popup");
                const provider = new GoogleAuthProvider();
                provider.setCustomParameters({ prompt: 'select_account' });
                const result = await signInWithPopup(auth, provider);
                firebaseUser = result.user;
            }

            if (firebaseUser) {
                console.log("✅ Google Auth Successful, checking strict roles...");
                const profileDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
                const exists = profileDoc.exists();
                const existingRole = exists ? profileDoc.data().role : null;
                
                if (intent === 'login') {
                    if (!exists) {
                        try { await firebaseUser.delete(); } catch(e) {}
                        await signOut(auth);
                        throw new Error('not_registered');
                    }
                    if (existingRole && existingRole !== role) {
                        await signOut(auth);
                        throw new Error(`wrong_role_login:${existingRole}`);
                    }
                } else if (intent === 'register') {
                    if (exists && existingRole && existingRole !== role) {
                        await signOut(auth);
                        throw new Error(`wrong_role_register:${existingRole}`);
                    }
                }
                
                await handleGoogleProfile(firebaseUser, role);
                localStorage.removeItem(ROLE_STORAGE_KEY);
            }
        } catch (error: any) {
            console.error("❌ loginWithGoogle global error:", error);
            const errorMsg = String(error.message || "").toLowerCase();
            if (!errorMsg.includes('wrong_role')) {
                setAuthError(`Erreur de connexion Google: ${error.message}`);
            }
            throw error; // Propagate to components
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            if (currentUser?.uid) {
                const updates: any = {};
                if (currentUser.role === 'livreur') {
                    // S'assurer que le livreur passe hors-ligne lors de la deconnexion
                    updates.isOnline = false;
                }
                // Supprimer uniquement le token FCM de CET appareil pour ne plus recevoir les notifs
                const myToken = localStorage.getItem('my_fcm_token') || currentUser.fcmToken;
                if (myToken) {
                    updates.fcmTokens = arrayRemove(myToken);
                }
                // On ne supprime plus fcmToken (string) car il peut appartenir à un autre appareil
                
                await updateDoc(doc(db, 'users', currentUser.uid), updates).catch(err => console.error("Error updating online status/token on logout:", err));
                localStorage.removeItem('my_fcm_token');
            }

            if (Capacitor.isNativePlatform()) {
                await FirebaseAuthentication.signOut();
            }
        } catch (error) {
            console.error("Error signing out from native Firebase Auth:", error);
        }
        setCurrentUser(null);
        setProfileLoaded(false);
        await signOut(auth);
    };

    const resetPassword = async (email: string) => {
        await sendPasswordResetEmail(auth, email);
    };

    const updateUserPassword = async (password: string) => {
        const user = auth.currentUser;
        if (user) {
            await updatePassword(user, password);
        }
    };

    const updateUserProfile = async (updates: Partial<User>) => {
        if (!currentUser) return;

        try {
            await updateDoc(doc(db, 'users', currentUser.uid), updates);
            setCurrentUser(prev => prev ? { ...prev, ...updates } : null);
        } catch (error) {
            console.error("Error updating profile:", error);
            throw error;
        }
    };

    const checkRestaurantStatus = async () => {
        if (!currentUser?.restaurantId) return;
        const snap = await getDoc(doc(db, 'restaurants', currentUser.restaurantId.trim()));
        if (snap.exists()) {
            const data = snap.data();
            const rawStatus = (data.status || 'pending').toLowerCase();
            const newStatus = (rawStatus === 'active' || rawStatus === 'approved') ? 'approved' : rawStatus;
            setCurrentUser(prev => prev ? { ...prev, restaurantStatus: newStatus as any } : null);

            if (newStatus === 'approved' && currentUser.uid) {
                // Ensure the users collection is synchronized so it doesn't infinitely loop on reload
                updateDoc(doc(db, 'users', currentUser.uid), { restaurantStatus: newStatus }).catch(console.error);
            }
        }
    };

    const refreshProfile = async () => {
        if (auth.currentUser) await fetchProfile(auth.currentUser);
    };

    return (
        <AuthContext.Provider value={{
            currentUser,
            loading,
            login,
            register,
            loginWithGoogle,
            logout,
            refreshProfile,
            checkRestaurantStatus,
            resetPassword,
            updateUserPassword,
            updateUserProfile,
            isPasswordRecovery,
            profileLoaded,
            authError,
            requestNotificationPermission,
            notificationPermission,
            pendingNotification,
            clearPendingNotification,
            devCreateTestAccount,
            pendingRoleMismatch,
            clearRoleMismatch
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};
